import ECSLink from 'components/editor/ECSLink';
import ECSSheetEvents from 'components/editor/ECSSheetEvents';
import ECSSheetMarkup from 'components/editor/ECSSheetMarkup';
import ECSSheetOverlay from 'components/editor/ECSSheetOverlay';
import ECSSpaceLayer from 'components/editor/ECSSpaceLayer';
import ECSTileSheetLayer from 'components/editor/ECSTileSheetLayer';
import AppStoreContext from 'contexts/AppStoreContext';
import ECSEditorContext from 'contexts/ECSEditorContext';
import CarnivoreSheet from 'editor/components/CarnivoreSheet';
import ToolOverlay from 'editor/components/ToolOverlay';
import Immutable, { OrderedMap } from 'immutable';
import React, {
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Link as LinkRecord, LinkId } from 'records/Link';
import { Markup as MarkupRecord } from 'records/Markup';
import { Sheet as SheetRecord } from 'records/Sheet';
import ECSKeyboardEvents from '../ECSKeyboardEvents';

type Props = PropsWithChildren<{
  documentid: string;
  pageid: string;
  sheet?: SheetRecord;
  width: number;
  height: number;
  withMarkups?: boolean;
  isInteractive?: boolean; // TBD - move to ECSEditorContext?
  drawingtilescloudfronturl: string;
  drawingmaxzoom: number;
  drawingminzoom: number;
  drawingtilesize: number;
}>;

// This component creates and manages the Carnivore "Sheet" component.
const TileSheet: FC<Props> = (props: Props) => {
  const {
    documentid,
    pageid,
    sheet,
    width,
    height,
    withMarkups,
    isInteractive,
    drawingtilescloudfronturl,
    drawingmaxzoom,
    drawingminzoom,
    drawingtilesize,
  } = props;
  const appStore = useContext(AppStoreContext);
  const { doc, carnCtx } = useContext(ECSEditorContext);
  const [sheetComp, setSheetComp] = useState<CarnivoreSheet | null>(null);
  const [tilesLoaded, setTilesLoaded] = useState(false);

  const { markups } = appStore.state;
  const maxScale = 2 ** (drawingmaxzoom - 1);

  const handleTilesLoaded = useCallback(() => {
    setTilesLoaded(true);
  }, []);

  async function createSheetEntity() {
    if (sheet) {
      await Promise.all(
        sheet.versions
          .map((v) => appStore.actions.getSheetMarkups(v.pageid))
          .toList()
      );
    } else {
      await appStore.actions.getSheetMarkups(pageid);
    }

    const carnSheet = carnCtx.ctx.createSheet({
      id: 'sheet-1',
      width: width * maxScale,
      height: height * maxScale,
    });

    const _sheetComp = new CarnivoreSheet(carnSheet);

    doc.doc.addSheet(carnSheet.id);
    doc.doc.zoomFullOnSheet(carnSheet.id);

    setSheetComp(_sheetComp);
  }

  useEffect(() => {
    appStore.actions.getLinks(pageid);
    createSheetEntity();
  }, []);

  useEffect(() => {
    return () => {
      if (sheetComp) {
        sheetComp.sheet.destroy();
      }
    };
  }, [sheetComp]);

  const sheetLinks = appStore.state.links
    .get(pageid, Immutable.OrderedMap<LinkId, LinkRecord>())
    ?.valueSeq()
    .toArray();
  /* ----------------------------------------- */
  const sheetMarkups = sheet
    ? ([] as MarkupRecord[]).concat(
        ...sheet.versions
          .map((v) => {
            return markups
              .get(v.pageid, OrderedMap<string, MarkupRecord>())
              .valueSeq()
              .toArray();
          })
          .toArray()
      )
    : markups
        .get(pageid, OrderedMap<string, MarkupRecord>())
        .valueSeq()
        .toArray();
  // ToolOverlay tags the entity when Toolbar is present
  const additionalComponents = [];
  if (isInteractive) {
    additionalComponents.push(new ToolOverlay());
  }
  return sheetComp ? (
    <>
      <ECSTileSheetLayer
        sheetComp={sheetComp}
        index={0}
        width={width}
        height={height}
        drawingtilescloudfronturl={drawingtilescloudfronturl}
        drawingmaxzoom={drawingmaxzoom}
        drawingminzoom={drawingminzoom}
        drawingtilesize={drawingtilesize}
        onTilesLoaded={handleTilesLoaded}
      />
      {tilesLoaded && (
        <ECSSpaceLayer
          sheetComp={sheetComp}
          index={1}
          width={width * maxScale}
          height={height * maxScale}
          documentid={documentid}
          pageid={pageid}
        />
      )}
      {withMarkups && tilesLoaded && (
        <ECSSheetOverlay
          key={pageid}
          sheetComp={sheetComp}
          id="markup-overlay"
          index={0}
          additionalComponents={additionalComponents}
        >
          {sheetMarkups.map((markup) => (
            <ECSSheetMarkup
              key={markup.markupid}
              markup={markup}
              sheetComp={sheetComp}
            />
          ))}
        </ECSSheetOverlay>
      )}
      <ECSSheetOverlay
        key="hyperlink-overlay-key"
        sheetComp={sheetComp}
        id="hyperlink-overlay"
        index={3}
        additionalComponents={additionalComponents}
      >
        {sheetLinks.map((link) => (
          <ECSLink key={link.linkid} link={link} />
        ))}
      </ECSSheetOverlay>
      <ECSKeyboardEvents />
      <ECSSheetEvents sheetComp={sheetComp} pageid={pageid} />
    </>
  ) : null;
};

export default TileSheet;
